import React, { useState, useEffect } from "react";
import axios from "axios";
import { Container, Row, Spinner, Alert, Tab } from "react-bootstrap";
import MSCDItem from "./StaticCovidDataItem";
import { TimeSeriesChart, toTimestamp } from "../modeling/components/TimeSeriesChart";

// Highcharts, как в графиках MFG (drag-zoom + «Сбросить масштаб»).
const variants = {
  visible: (custom) => ({ opacity: 1, y: 0, transition: { delay: custom * 0.2 } }),
  hidden: { opacity: 0, y: 100 },
};

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

const dayPoints = (rows, key) =>
  rows.map((r) => [toTimestamp(r.date), toInt(r[key])]).filter((p) => p[0] != null);

const StaticCovidData = (props) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pickedDate, setPickedDate] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    axios
      .get(props.region.hr)
      .then((res) => {
        if (!alive) return;
        const data = Array.isArray(res.data) ? res.data : [];
        setRows(data);
        setPickedDate(data.length ? data[data.length - 1].date : ""); // по умолчанию последняя дата
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        console.log(err);
        setError(true);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [props.region.hr]);

  const minDate = rows.length ? rows[0].date : undefined;
  const maxDate = rows.length ? rows[rows.length - 1].date : undefined;
  const current = rows.find((r) => r.date === pickedDate);

  const box = (name, key) => {
    if (loading) return { name, loading: true };
    const v = current ? toInt(current[key]) : null;
    return v === null
      ? { name, noData: true, lastDatadate: pickedDate }
      : { name, value: v, lastDatadate: pickedDate };
  };

  const lasts = [
    { id: 1, ...box("случаев заражения", "new_diagnoses") },
    { id: 2, ...box("случаев выздоровления", "new_recoveries") },
    { id: 3, ...box("летальных исходов", "new_deaths") },
  ];

  // Две оси Y: слева крупные накопительные (заболеваемость/выздоровления),
  // справа мелкие (новые за день/летальные) — каждая ось автоскейлится под свои ряды.
  const series = [
    { name: "Заболеваемость (сум.)", data: dayPoints(rows, "cum_diagnoses"), color: "#212529", yAxis: 0 },
    { name: "Выздоровления (сум.)", data: dayPoints(rows, "cum_recoveries"), color: "#fc8dd6", yAxis: 0 },
    { name: "Новые случаи за день", data: dayPoints(rows, "new_diagnoses"), color: "#00bfff", yAxis: 1 },
    { name: "Летальные исходы (сум.)", data: dayPoints(rows, "cum_deaths"), color: "#ff0000", yAxis: 1 },
  ];

  const yAxes = [
    { title: { text: "Заболеваемость / выздоровления" }, min: 0, startOnTick: false, endOnTick: false },
    { title: { text: "Новые за день / летальные" }, min: 0, opposite: true, startOnTick: false, endOnTick: false },
  ];

  return (
    <Tab.Pane eventKey={props.region.eventKey}>
      <div className="mx-3 my-3">
        <div className="mb-3 d-flex align-items-center" style={{ gap: 8 }}>
          <span className="text-muted">По данным на</span>
          <input
            type="date"
            className="form-control"
            style={{ maxWidth: 190 }}
            min={minDate}
            max={maxDate}
            value={pickedDate}
            onChange={(e) => setPickedDate(e.target.value)}
            disabled={loading}
          />
        </div>
        <Container>
          <Row style={{ width: "100%" }}>
            {lasts.map((item, index) => (
              <MSCDItem
                initial="hidden"
                whileInView="visible"
                viewport={{ amount: 0.05 }}
                custom={index + 1}
                variants={variants}
                key={item.id}
                last={item}
              />
            ))}
          </Row>
        </Container>
        <div className="my-4">
          {loading ? (
            <div style={{ height: 420, position: "relative" }}>
              <Spinner style={{ position: "absolute", top: "45%", left: "50%" }} animation="border" variant="info" />
            </div>
          ) : error ? (
            <Alert variant="danger" className="my-5">
              <Alert.Heading>Ошибка загрузки</Alert.Heading>
              Сервер временно не отвечает, пожалуйста, обновите страницу или повторите попытку позже.
            </Alert>
          ) : (
            <TimeSeriesChart
              title="Статистические данные COVID-19"
              series={series}
              yAxis={yAxes}
              language="ru"
            />
          )}
        </div>
      </div>
    </Tab.Pane>
  );
};

export default StaticCovidData;
